import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../../utils/api'
import { sortConditions, sortPrintings } from '../../../utils/variantOrdering'
import { CUSTOM_CATEGORIES } from '../../../catalog/categoryRegistry'
import type { CardSet, Game } from './inventoryTypes'
import { fetchStoreTags, storeTagsQueryKey } from '../../../components/products/productDrawer/productTags'

export type InventoryFilterOptions = {
  games: Game[]
  /** Every set the store has stock in; narrowed to the chosen games when there are any. */
  sets: CardSet[]
  allSets: CardSet[]
  conditions: string[]
  printings: string[]
  categories: Array<{ slug: string; label: string }>
  /** Storefront tags in use (system and manual), most products first. */
  tags: string[]
}

/** Category picklist: catalogue singles plus every custom-product category, as the registry names them. */
const CATEGORY_OPTIONS = [{ slug: 'single_card', label: 'Single' }, ...CUSTOM_CATEGORIES.map(({ slug, label }) => ({ slug, label }))]

/**
 * What the filter pickers offer: the games / sets / conditions / printings this store has stock
 * in (GET /inventory/filters), narrowed to one game or set when exactly one is chosen.
 */
export function useInventoryFilterOptions(selectedGames: string[], selectedSets: string[]): InventoryFilterOptions {
  const bootstrap = useQuery({
    queryKey: ['inventory', 'filters', 'bootstrap'],
    queryFn: async ({ signal }) => {
      try {
        const response = await api.get('/inventory/filters', { signal })
        const data = response.data.data
        let gamesList: Game[] = data?.games || []
        if (!data?.games || data.games.length === 0) {
          const gamesResponse = await api.get('/games', { signal })
          gamesList = gamesResponse.data.data?.games || []
        }
        return {
          games: gamesList,
          allSets: (data?.sets || []) as CardSet[],
          conditions: Array.isArray(data?.conditions) ? sortConditions(data.conditions) : [],
          printings: Array.isArray(data?.printings) ? sortPrintings(data.printings) : [],
        }
      } catch (err) {
        console.error('Error loading inventory filters:', err)
        try {
          const gamesResponse = await api.get('/games', { signal })
          return { games: (gamesResponse.data.data?.games || []) as Game[], allSets: [] as CardSet[], conditions: [] as string[], printings: [] as string[] }
        } catch {
          return { games: [] as Game[], allSets: [] as CardSet[], conditions: [] as string[], printings: [] as string[] }
        }
      }
    },
    staleTime: 5 * 60 * 1000,
  })

  // One game or one set chosen: its own conditions and printings. Several: the store-wide lists.
  const scopeSet = selectedSets.length === 1 ? selectedSets[0] : ''
  const scopeGame = !scopeSet && selectedGames.length === 1 ? selectedGames[0] : ''
  const scoped = useQuery({
    queryKey: ['inventory', 'filters', 'scoped', scopeGame, scopeSet],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams(scopeSet ? { setId: scopeSet } : { gameId: scopeGame })
      const response = await api.get(`/inventory/filters?${params.toString()}`, { signal })
      return response.data?.data as { conditions?: string[]; printings?: string[] } | undefined
    },
    enabled: Boolean(scopeGame || scopeSet),
    staleTime: 2 * 60 * 1000,
  })

  // The Tag filter's picklist: the Tags page's own list (system and manual, by product count).
  const storeTags = useQuery({
    queryKey: storeTagsQueryKey,
    queryFn: ({ signal }) => fetchStoreTags(signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return useMemo(() => {
    const data = bootstrap.data
    const tagEntries = [...(storeTags.data?.system ?? []), ...(storeTags.data?.manual ?? [])]
      .filter((entry) => entry.products > 0)
      .sort((a, b) => b.products - a.products || a.tag.localeCompare(b.tag))
    const tags = [...new Map(tagEntries.map((entry) => [entry.tag.toLowerCase(), entry.tag])).values()]
    const allSets = data?.allSets ?? []
    const sets = selectedGames.length > 0 ? allSets.filter((set) => set.gameId && selectedGames.includes(set.gameId)) : allSets
    const scopedData = scopeGame || scopeSet ? scoped.data : undefined
    return {
      games: data?.games ?? [],
      sets,
      allSets,
      conditions: scopedData?.conditions ? sortConditions(scopedData.conditions) : data?.conditions ?? [],
      printings: scopedData?.printings ? sortPrintings(scopedData.printings) : data?.printings ?? [],
      categories: CATEGORY_OPTIONS,
      tags,
    }
  }, [bootstrap.data, scoped.data, scopeGame, scopeSet, selectedGames, storeTags.data])
}
